import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, MessageSquare, Loader2, Wand2 } from "lucide-react";

export default function DesignApproval() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/design-requests/approve/${token}`).then((r) => setReq(r.data))
      .catch((e) => setError(apiError(e.response?.data?.detail) || "Request not found"));
  }, [token]);

  const act = async (action) => {
    if (action === "changes" && !note.trim()) { toast.error("Tell us what to change"); return; }
    setBusy(true);
    try {
      await api.post(`/design-requests/approve/${token}`, { action, note: note.trim() || null });
      setDone(action);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-zinc-400" data-testid="approval-error">{error}</div>;
  if (!req) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-zinc-500"><Loader2 className="animate-spin mx-auto" /></div>;

  const latest = [...(req.deliverables || [])].reverse().find((d) => d.kind === "draft" || d.kind === "ai_draft")
    || (req.deliverables || [])[req.deliverables.length - 1];

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14" data-testid="design-approval-page">
      <div className="flex items-center gap-2 text-[#ff6a13] font-mono-p text-xs uppercase tracking-widest"><Wand2 size={14} />Design proof</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-2">Review your design</h1>
      <p className="text-zinc-400 mt-2">{req.tier_name} · Order {req.order_number}</p>

      {latest ? (
        <div className="mt-6 bg-[#12141a] border border-zinc-800 rounded-2xl p-4 checker flex items-center justify-center">
          <img src={fileUrl(latest.url)} alt="draft" className="max-w-full max-h-[420px] object-contain" data-testid="approval-draft-img" />
        </div>
      ) : <p className="mt-6 text-zinc-500">No draft shared yet.</p>}

      {done ? (
        <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="approval-done">
          <Check className="mx-auto text-emerald-400" size={28} />
          <p className="text-white font-semibold mt-2">{done === "approve" ? "Thanks — your approval is in!" : "Thanks — we've received your change request."}</p>
          <p className="text-zinc-400 text-sm mt-1">Our team will take it from here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note" rows={3} placeholder="Optional note, or describe changes you'd like…" className="bg-[#1e222d] border-zinc-700 text-white" />
          <div className="flex gap-3">
            <Button onClick={() => act("approve")} disabled={busy} data-testid="approval-approve" className="flex-1 bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-11 font-semibold"><Check size={16} className="mr-1" />Approve</Button>
            <Button onClick={() => act("changes")} disabled={busy} variant="outline" data-testid="approval-changes" className="flex-1 border-zinc-700 bg-transparent text-white hover:bg-zinc-900 rounded-full h-11 font-semibold"><MessageSquare size={16} className="mr-1" />Request changes</Button>
          </div>
        </div>
      )}
    </div>
  );
}
